import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { RefreshCw, X } from "lucide-react";
import { applyWaitingUpdate } from "@/lib/pwa";

/**
 * Tells someone when a new build is cached and waiting for a reload.
 *
 * This used to also carry an "Install TrendingZ" add-to-home-screen prompt. That
 * is gone: there is a real Play listing now, linked from the nav menu and the
 * footer, so the banner was a second and worse install offer competing with it -
 * and it sat over the video cards until dismissed.
 *
 * The update banner stays, because the web app has no other way to say that a
 * newer version is already downloaded and one reload away.
 */
export const InstallPrompt = () => {
  const [updateRegistration, setUpdateRegistration] = useState(null);

  useEffect(() => {
    const onUpdateReady = (e) => setUpdateRegistration(e.detail);
    window.addEventListener("tz-update-ready", onUpdateReady);
    return () => window.removeEventListener("tz-update-ready", onUpdateReady);
  }, []);

  return (
    <AnimatePresence>
      {updateRegistration && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          className="tz-safe-bottom fixed bottom-4 left-1/2 -translate-x-1/2 z-[90] w-[min(22rem,calc(100vw-2rem))]"
          data-testid="install-prompt"
        >
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl glass border border-white/10 shadow-xl">
            <RefreshCw className="w-5 h-5 text-orange-500 shrink-0" />
            <p className="text-sm flex-1 min-w-0">A new version is ready</p>
            <button
              onClick={() => applyWaitingUpdate(updateRegistration)}
              className="px-3 py-1.5 rounded-lg bg-orange-500 text-black text-xs font-bold hover:opacity-90 transition-opacity"
            >
              Reload
            </button>
            <button
              onClick={() => setUpdateRegistration(null)}
              className="p-1 text-white/40 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
